/**
 * Core chemical calculations for hydroponics batch preparation
 * Ported from the original Pascal implementation
 */

import { Element } from '../types/Element';
import { Salt, SaltElement } from '../types/Salt';

// Constants
export const ELEMENTS_INDEX: Record<string, number> = {
  'N (NO3-)': 0,
  'N (NH4+)': 1,
  'P': 2,
  'K': 3,
  'Mg': 4,
  'Ca': 5,
  'S': 6,
  'Fe': 7,
  'Mn': 8,
  'Zn': 9,
  'B': 10,
  'Cu': 11,
  'Si': 12,
  'Mo': 13,
  'Na': 14,
  'Cl': 15
};

export const ELEMENT_SYMBOLS = Object.keys(ELEMENTS_INDEX);

/**
 * Returns the molar mass for each element in the same order as ELEMENTS_INDEX
 */
export function getMolarMasses(): number[] {
  const molarMass = [
    14.007,  // N (NO3-) [0]
    14.007,  // N (NH4+) [1]
    30.974,  // P [2]
    39.098,  // K [3]
    24.305,  // Mg [4]
    40.078,  // Ca [5]
    32.066,  // S [6]
    55.845,  // Fe [7]
    54.938,  // Mn [8]
    65.409,  // Zn [9]
    10.811,  // B [10]
    63.546,  // Cu [11]
    28.086,  // Si [12]
    95.94,   // Mo [13]
    22.990,  // Na [14]
    35.453   // Cl [15]
  ];
  
  return molarMass;
}

/**
 * Returns the valence/equivalents for each element in the same order as ELEMENTS_INDEX
 */
export function getEquivalents(): number[] {
  const equivalents = [
    1,  // N (NO3-) [0]
    1,  // N (NH4+) [1]
    3,  // P [2]
    1,  // K [3]
    2,  // Mg [4]
    2,  // Ca [5]
    2,  // S [6]
    2,  // Fe [7]
    2,  // Mn [8]
    2,  // Zn [9]
    1,  // B [10]
    2,  // Cu [11]
    2,  // Si [12]
    2,  // Mo [13]
    1,  // Na [14]
    1   // Cl [15]
  ];
  
  return equivalents;
}

/**
 * Round a number to a specified decimal places
 * Equivalent to Pascal's round2 function
 */
export function round(value: number, decimals: number): number {
  return Number(Math.round(parseFloat(value + 'e' + decimals)) + 'e-' + decimals);
}

/**
 * Calculate nutrient ratios (like N:P:K, Ca:Mg, etc.)
 * @param first First element symbol (e.g. "N", "Ca")
 * @param second Second element symbol (e.g. "P", "Mg")
 * @param third Third element symbol (optional, e.g. "K")
 * @param items Number of elements in the ratio (2 or 3)
 * @param concentrations Element concentrations in ppm or mg/L
 */
export function getRatio(
  first: string,
  second: string,
  third: string,
  items: number,
  concentrations: Record<string, number>
): string {
  if (items < 2 || items > 3) {
    return 'no result';
  }

  const names = [first, second, third, ''];
  const temp: number[] = [0, 0, 0, 0];
  const special: number[] = [-1, -1, -1];

  // Handle special cases
  for (let i = 0; i < 3; i++) {
    if (names[i] === 'N') {
      names[i] = 'N (NO3-)';
      names[3] = 'N (NH4+)';
      special[0] = i;
    } else if (names[i] === 'P2O5') {
      names[i] = 'P';
      special[1] = i;
    } else if (names[i] === 'K2O') {
      names[i] = 'K';
      special[2] = i;
    }
  }

  // Get concentrations for the elements in the ratio
  for (let j = 0; j < 4; j++) {
    if (names[j] && concentrations[names[j]] !== undefined) {
      temp[j] = concentrations[names[j]];
    }
  }

  // Check if we have all the required values
  if (temp[0] <= 0 || temp[1] <= 0 || (items === 3 && temp[2] <= 0)) {
    return 'insufficient data';
  }

  // Handle special cases
  if (special[0] !== -1 && names[special[0]] === 'N (NO3-)') {
    temp[special[0]] = temp[3] + temp[special[0]];
  }

  if (special[1] !== -1 && names[special[1]] === 'P') {
    temp[special[1]] = temp[special[1]] * 2.2915; // Convert P to P2O5 if needed
  }

  if (special[2] !== -1 && names[special[2]] === 'K') {
    temp[special[2]] = temp[special[2]] * 1.2047; // Convert K to K2O if needed
  }

  // Calculate ratios
  const ratio = [
    1,
    round(temp[1] / temp[0], 2),
    round(temp[2] / temp[0], 2)
  ];

  // Format result
  if (items === 2) {
    return `${ratio[0]}: ${ratio[1]}`;
  } else {
    return `${ratio[0]}: ${ratio[1]}: ${ratio[2]}`;
  }
}

/**
 * HydroBuddy Concentration Factor Management
 * Based on hb_main.pas concentration factor calculations
 */
export interface ConcentrationFactors {
  ppm: number;    // = 1 (base unit)
  mM: number;     // = molar_mass
  M: number;      // = 1000 * molar_mass
  mN: number;     // = molar_mass / equivalents
}

/**
 * Calculate concentration factors for HydroBuddy algorithm
 * Based on Pascal implementation in hb_main.pas
 */
export function getConcentrationFactors(elementSymbol: string): ConcentrationFactors {
  const index = ELEMENTS_INDEX[elementSymbol];
  if (index === undefined) {
    throw new Error(`Unknown element: ${elementSymbol}`);
  }

  const molarMass = getMolarMasses()[index];
  const equivalent = getEquivalents()[index];

  return {
    ppm: 1,                           // Base unit (ppm = mg/L)
    mM: molarMass,                    // Millimolar
    M: 1000 * molarMass,              // Molar
    mN: molarMass / equivalent        // Millinormal
  };
}

/**
 * Convert concentration from one unit to another using HydroBuddy factors
 * @param value Value to convert
 * @param elementSymbol Element symbol
 * @param fromUnit Source unit ('ppm', 'mM', 'M', 'mN')
 * @param toUnit Target unit ('ppm', 'mM', 'M', 'mN')
 */
export function convertConcentration(
  value: number,
  elementSymbol: string,
  fromUnit: string,
  toUnit: string
): number {
  if (fromUnit === toUnit) {
    return value;
  }

  const factors = getConcentrationFactors(elementSymbol);

  // Convert to ppm first (base unit)
  let ppmValue: number;
  switch (fromUnit) {
    case 'ppm':
      ppmValue = value;
      break;
    case 'mM':
    case 'mmol/L': // Support both formats
      ppmValue = value * factors.mM;
      break;
    case 'M':
      ppmValue = value * factors.M;
      break;
    case 'mN':
    case 'meq/L': // milliequivalents per liter (same as mN)
      ppmValue = value * factors.mN;
      break;
    default:
      throw new Error(`Unknown source unit: ${fromUnit}`);
  }

  // Convert from ppm to target unit
  switch (toUnit) {
    case 'ppm':
      return ppmValue;
    case 'mM':
    case 'mmol/L': // Support both formats
      return ppmValue / factors.mM;
    case 'M':
      return ppmValue / factors.M;
    case 'mN':
    case 'meq/L': // milliequivalents per liter (same as mN)
      return ppmValue / factors.mN;
    default:
      throw new Error(`Unknown target unit: ${toUnit}`);
  }
}


/**
 * Calculate the amount of an element contributed by a salt
 * @param salt The salt
 * @param amount Amount of salt in grams
 * @param volume Volume in liters
 */
export function calculateElementContribution(
  salt: Salt, 
  amount: number, 
  volume: number
): Record<string, number> {
  const elementContributions: Record<string, number> = {};
  
  if (!salt.elements || salt.elements.length === 0) {
    return elementContributions;
  }
  
  // Calculate the contribution of each element in the salt
  salt.elements.forEach(element => {
    const { symbol, percentage } = element;
    
    // Calculate contribution in ppm (mg/L)
    // (salt amount in grams * element percentage / 100) / volume in liters * 1000 mg/g
    const contribution = (amount * percentage / 100) / volume;
    
    elementContributions[symbol] = contribution;
  });
  
  return elementContributions;
}

/**
 * Calculate solubility of a salt based on temperature
 * @param baseSolubility Base solubility of the salt at 20°C (g/L)
 * @param temperature Temperature in °C
 */
export function calculateTemperatureAdjustedSolubility(
  baseSolubility: number,
  temperature: number
): number {
  // Simple linear model for estimating solubility based on temperature
  // Most salts increase solubility with temperature at roughly 1-3% per °C
  // This is a simplified approximation
  if (temperature === 20 || baseSolubility <= 0) {
    return baseSolubility;
  }
  
  const tempDiff = temperature - 20; // Difference from reference temp (20°C)
  const adjustmentFactor = 0.02; // 2% change per degree C (average)
  
  return baseSolubility * (1 + tempDiff * adjustmentFactor);
}

/**
 * Check for salt incompatibilities in a solution
 * @param salts Array of salts in the solution
 */
export function checkIncompatibilities(
  salts: {saltId: string, name: string}[]
): {saltA: string, saltB: string, severity: 'warning'|'error', message: string}[] {
  const incompatibilities: {saltA: string, saltB: string, severity: 'warning'|'error', message: string}[] = [];
    // Define incompatible salt combinations
  const incompatiblePairs: Array<{
    patterns: Array<{ namePattern: RegExp; idPattern: RegExp }>;
    severity: 'warning' | 'error';
    message: string;
  }> = [
    // Calcium salts and phosphate/sulfate salts
    {
      patterns: [
        { namePattern: /calcium|Ca\(NO3\)2/, idPattern: /calcium/ },
        { namePattern: /phosphate|PO4|H3PO4/, idPattern: /phosphate/ }
      ],
      severity: 'error',
      message: 'Calcium and phosphate will form insoluble precipitates'
    },
    {
      patterns: [
        { namePattern: /calcium|Ca\(NO3\)2/, idPattern: /calcium/ },
        { namePattern: /sulfate|SO4/, idPattern: /sulfate/ }
      ],
      severity: 'error',
      message: 'Calcium and sulfate can form insoluble precipitates (gypsum)'
    },
    // Iron chelates and phosphate
    {
      patterns: [
        { namePattern: /iron|Fe-EDTA|Fe-DTPA|Fe-EDDHA/, idPattern: /iron|fe-/ },
        { namePattern: /phosphate|PO4/, idPattern: /phosphate/ }
      ],
      severity: 'warning',
      message: 'Iron chelates may be destabilized by phosphates'
    },
    // Manganese and phosphate
    {
      patterns: [
        { namePattern: /manganese|Mn/, idPattern: /manganese|mn/ },
        { namePattern: /phosphate|PO4/, idPattern: /phosphate/ }
      ],
      severity: 'warning',
      message: 'Manganese may form precipitates with phosphates'
    }
  ];
  
  // Check each incompatible pair against our salt list
  incompatiblePairs.forEach(pair => {
    const [patternA, patternB] = pair.patterns;
    
    // Find salts that match pattern A and pattern B
    const saltMatchesA = salts.filter(salt => 
      patternA.namePattern.test(salt.name.toLowerCase()) || 
      (salt.saltId && patternA.idPattern.test(salt.saltId.toLowerCase()))
    );
    
    const saltMatchesB = salts.filter(salt => 
      patternB.namePattern.test(salt.name.toLowerCase()) || 
      (salt.saltId && patternB.idPattern.test(salt.saltId.toLowerCase()))
    );
    
    // If we have matches for both patterns, we have incompatible salts
    if (saltMatchesA.length > 0 && saltMatchesB.length > 0) {
      saltMatchesA.forEach(saltA => {
        saltMatchesB.forEach(saltB => {
          incompatibilities.push({
            saltA: saltA.name,
            saltB: saltB.name,
            severity: pair.severity as 'warning' | 'error',
            message: pair.message
          });
        });
      });
    }
  });
  
  return incompatibilities;
}

/**
 * Calculate the total dissolved solids (TDS) from salt concentrations
 * @param salts Array of salts with concentrations in g/L
 */
export function calculateTotalDissolvedSolids(
  salts: {concentration: number}[]
): number {
  return salts.reduce((total, salt) => total + salt.concentration, 0);
}

/**
 * DEPRECATED: Estimate the EC (Electrical Conductivity) based on TDS
 *
 * ⚠️  WARNING: This function is DEPRECATED and provides only rough estimation.
 * Use ECCalculationService.calculate() instead for accurate Pascal HydroBuddy algorithms.
 *
 * @deprecated Use ECCalculationService.calculate() instead
 * @param tds Total Dissolved Solids in g/L
 */
export function estimateEC(tds: number): number {
  console.warn('⚠️  DEPRECATED: estimateEC() is deprecated. Use ECCalculationService.calculate() instead.');

  // Rough estimation: EC (mS/cm) ≈ TDS (g/L) * 1.5
  // This is kept for backward compatibility only
  return tds * 1.5;
}
